import { ScaleElement } from '@scale/base-element';
import { fetchTextWithTimeout } from '@scale/utilities';

const CACHE_MAX = 15;

class ScaleQuickView extends ScaleElement {
  static requiredRefs = ['dialog', 'content', 'loading'];

  #cache = new Map();
  #currentRequest = null;
  #abortController = null;

  setup() {
    // Global click delegation for [data-quick-view] buttons
    this.onDocument('click', (e) => {
      const trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      this.load(trigger.dataset.productHandle, trigger.dataset.variantId);
    });

    // Close on cart update (user sees cart drawer instead)
    this.onDocument('shopify:cart:lines-update', (event) => {
      if (event.action !== 'add') return;
      event.promise?.then(() => {
        if (this.ref('dialog').open) this.#close();
      }).catch(() => {});
    });

    // Backdrop click + Escape (close button handled by on:click="close" declarative)
    const dialog = this.ref('dialog');
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.#close();
    }, { signal: this.signal });

    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#close();
    }, { signal: this.signal });
  }

  close() {
    this.#close();
  }

  async load(handle, variantId) {
    if (!handle || !/^[a-z0-9\-]+$/.test(handle)) return;
    const requestKey = variantId ? `${handle}:${variantId}` : handle;
    this.#currentRequest = requestKey;

    const content = this.ref('content');
    const loading = this.ref('loading');
    const link = this.ref('product-link');

    // Update "View full details" link
    let url = `${window.Shopify.routes.root}products/${handle}`;
    if (variantId) url += `?variant=${variantId}`;
    if (link) link.href = url;

    // Show loading, open dialog
    loading.hidden = false;
    content.innerHTML = '';
    const dialog = this.ref('dialog');
    dialog.setAttribute('aria-busy', 'true');
    if (!dialog.open) dialog.showModal();

    this.#abortController?.abort();
    this.#abortController = null;

    // Check cache
    if (this.#cache.has(requestKey)) {
      if (!this.#inject(this.#cache.get(requestKey))) this.#showError();
      return;
    }

    // Fetch product section
    this.#abortController = new AbortController();

    try {
      const fetchUrl = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
      fetchUrl.searchParams.set('section_id', 'main-product');
      if (variantId) fetchUrl.searchParams.set('variant', variantId);
      const { response, text: html } = await fetchTextWithTimeout(fetchUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Only inject if still the current request
      if (this.#currentRequest === requestKey) {
        if (!this.#inject(html)) throw new Error('Malformed quick view response');

        // Cache only validated section HTML.
        if (this.#cache.size >= CACHE_MAX) {
          this.#cache.delete(this.#cache.keys().next().value);
        }
        this.#cache.set(requestKey, html);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (this.#currentRequest === requestKey) this.#showError();
    }
  }

  #inject(html) {
    const content = this.ref('content');
    const loading = this.ref('loading');

    // Parse and extract section content
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('[class*="product-page"], .main-product, section > div');

    if (!section) {
      return false;
    }

    content.innerHTML = section.outerHTML;
    loading.hidden = true;
    this.ref('dialog').removeAttribute('aria-busy');

    // Re-execute module scripts (innerHTML doesn't execute scripts)
    for (const script of content.querySelectorAll('script[type="module"]')) {
      const clone = document.createElement('script');
      clone.type = 'module';
      if (script.src) clone.src = script.src;
      else clone.textContent = script.textContent;
      script.replaceWith(clone);
    }

    // Prevent variant picker from modifying page URL
    const picker = content.querySelector('product-variant-picker');
    if (picker) picker.setAttribute('data-skip-url-update', '');

    return true;
  }

  #showError() {
    const message = document.createElement('p');
    message.className = 'quick-view__error';
    message.setAttribute('role', 'status');
    message.textContent = this.dataset.errorMessage;
    this.ref('content').replaceChildren(message);
    this.ref('loading').hidden = true;
    this.ref('dialog').removeAttribute('aria-busy');
  }

  #close() {
    const dialog = this.ref('dialog');
    this.#abortController?.abort();
    this.#abortController = null;
    this.#currentRequest = null;
    dialog.removeAttribute('aria-busy');
    if (!dialog.open) return;
    dialog.close();
  }
}

if (!customElements.get('scale-quick-view')) {
  customElements.define('scale-quick-view', ScaleQuickView);
}
